<template>
  <SectionSummary>
    <SectionSummaryRow v-for="summary in firstSectionSummaries" :key="summary.name">
      <template #description>
        <SectionSummaryText :text="getSummaryDisplayName(summary)" />
      </template>
      <template #value>
        <SectionSummaryText :text="getSummaryValueText(summary)" />
      </template>
    </SectionSummaryRow>
    <SectionSummaryDivider v-if="firstSectionSummaries.length" />
    <SectionSummaryRow v-if="showTotalPriceSummary">
      <template #description>
        <SectionSummaryText :text="getSummaryDisplayName(totalPriceSummary)" />
      </template>
      <template #value>
        <SectionSummaryText
          :text="getSummaryValueText(totalPriceSummary)"
          :data-external="`editor-pricing-table-section-summary-extended-summary-total-price-value-text`"
          align-right
        />
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="showDiscount">
      <template #description>
        <SectionSummaryText :text="sectionSummary.discount.displayName" />
      </template>
      <template #value>
        <SectionSummaryText :text="discountValueText" red align-right />
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="showTax">
      <template #description>
        <SectionSummaryText :text="sectionSummary.tax.displayName" />
      </template>
      <template #value>
        <SectionSummaryText :text="taxValueText" align-right />
      </template>
    </SectionSummaryRow>
    <template v-if="showTax && showTaxGrouping">
      <SectionSummaryRow v-for="[taxRate, taxValue] in groupedTaxes.flatFees" :key="taxRate">
        <template #description>
          <SectionSummaryText
            :data-external="`pricing-table-summary-tax-group-${taxRate}-rate`"
            :text="getTaxGroupName(taxRate, true)"
            small
            indent
          />
        </template>
        <template #value>
          <SectionSummaryText
            :data-external="`pricing-table-summary-tax-group-${taxRate}-value`"
            :text="getCurrenyFormattedValue(taxValue)"
            small
            align-right
          />
        </template>
      </SectionSummaryRow>
      <SectionSummaryRow v-for="[taxRate, taxValue] in groupedTaxes.taxes" :key="taxRate">
        <template #description>
          <SectionSummaryText
            :data-external="`pricing-table-summary-tax-group-${taxRate}-rate`"
            :text="getTaxGroupName(taxRate, false)"
            small
            indent
          />
        </template>
        <template #value>
          <SectionSummaryText
            :data-external="`pricing-table-summary-tax-group-${taxRate}-value`"
            :text="getCurrenyFormattedValue(taxValue)"
            small
            align-right
          />
        </template>
      </SectionSummaryRow>
    </template>
    <SectionSummaryRow>
      <template #description>
        <SectionSummaryText :text="sectionSummary.price.displayName" bold />
      </template>
      <template #value>
        <SectionSummaryText :text="subTotalValueText" bold align-right />
      </template>
    </SectionSummaryRow>
  </SectionSummary>
</template>
<script lang="ts" setup>
import type { Field, PricingTable } from '@getaccept/editor-lib';
import { computed, toRefs } from 'vue';
import { usePricingTableSectionSummary } from '@getaccept/editor-lib/src/composables/pricing-table-section-summary.composable';
import SectionSummary from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummary.vue';
import SectionSummaryRow from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryRow.vue';
import SectionSummaryText from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryText.vue';
import SectionSummaryDivider from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryDivider.vue';
import { useInjectEditorViewState } from '../../composables/editor-view-state.composable';

const { productProperties } = useInjectEditorViewState();

const props = defineProps<{
  pricingTable: PricingTable;
  sectionId: string;
  fields: Field[];
}>();

const { pricingTable, sectionId, fields } = toRefs(props);

const {
  sectionSummary,
  showDiscount,
  showTax,
  discountValueText,
  taxValueText,
  subTotalValueText,
  enabledSummaries,
  getSummaryDisplayName,
  getSummaryValueText,
  getTaxGroupName,
  showTaxGrouping,
  groupedTaxes,
  getCurrenyFormattedValue,
  totalPriceSummary,
  showTotalPriceSummary,
} = usePricingTableSectionSummary(pricingTable, sectionId, fields, productProperties);

const firstSectionSummaries = computed(() =>
  enabledSummaries.value.filter(({ columnId }) => columnId !== totalPriceSummary.value?.columnId)
);
</script>
